import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import PopoverTabs, { PopoverTab } from '@components/v2/ui/PopoverTabs/PopoverTabs';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { CatalogTestIds } from '../../../../utils/testIds/catalog.testIds';
import { useTranslation } from '../../../translation/TranslationContext';
import {
  CatalogCreationAccessCatalogDates,
  CatalogCreationAccessEntitiesToAdd,
  CatalogCreationAccessFieldName,
  CatalogCreationAccessRow,
  CatalogCreationAccessRowDateChange,
  CatalogCreationAccessSelection,
  CatalogCreationAccessTab,
} from '../catalogCreationAccess.types';
import { useCatalogAccessPermissions } from '../hooks/useCatalogAccessPermissions';
import CatalogCreationAccessAddModal from './CatalogCreationAccessAddModal';
import CatalogCreationAccessTable from './CatalogCreationAccessTable';

interface CatalogAccessEditorProps {
  tabs: CatalogCreationAccessTab[];
  selectedField: CatalogCreationAccessFieldName;
  rows: CatalogCreationAccessRow[];
  emptyLabel: LangEnum | null;
  catalogDates: CatalogCreationAccessCatalogDates;
  listedEntityIds: string[];
  maxVisibleRows?: number;
  isReadOnly?: boolean;
  catalogId?: string;
  selection?: CatalogCreationAccessSelection;
  onEndReached?: () => void;
  onSelectTab: (tabId: string) => void;
  onChangeRowDate: (rowDateChange: CatalogCreationAccessRowDateChange) => void;
  onRemoveRow: (entityId: string) => void;
  onAddEntities: (entitiesToAdd: CatalogCreationAccessEntitiesToAdd) => void;
}

const CatalogAccessEditor = ({
  tabs,
  selectedField,
  rows,
  emptyLabel,
  catalogDates,
  listedEntityIds,
  maxVisibleRows,
  isReadOnly,
  catalogId,
  selection,
  onEndReached,
  onSelectTab,
  onChangeRowDate,
  onRemoveRow,
  onAddEntities,
}: CatalogAccessEditorProps) => {
  const { translate } = useTranslation();
  const { canAddEntity, canRemoveEntity, canEditDates } = useCatalogAccessPermissions({
    isReadOnly,
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleAddEntities = (entitiesToAdd: CatalogCreationAccessEntitiesToAdd) => {
    onAddEntities(entitiesToAdd);
    setIsAddModalOpen(false);
  };

  return (
    <>
      <Stack
        sx={(theme) => ({
          rowGap: 1.5,
          padding: 1.5,
          borderRadius: `${theme.shape.borderRadius2xl}px`,
          border: `0.5px solid ${theme.palette.custom.border.primary}`,
        })}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', columnGap: 1, justifyContent: 'space-between' }}
        >
          <PopoverTabs value={selectedField} onChange={onSelectTab}>
            {tabs.map((tab) => (
              <PopoverTab
                key={tab.id}
                value={tab.id}
                test-id={tab.testId}
                label={
                  <Typography noWrap component="span" variant="inherit">
                    {translate(tab.label)}
                    <Typography
                      component="span"
                      variant="micro1"
                      sx={{ ml: 0.5, color: 'text.secondary' }}
                    >
                      {tab.count}
                    </Typography>
                  </Typography>
                }
              />
            ))}
          </PopoverTabs>
          {canAddEntity && (
            <Button
              variant="text"
              color="secondary"
              size="small"
              sx={{ flexShrink: 0 }}
              testId={CatalogTestIds.CATALOG_CREATION_MODAL_ACCESS_ADD}
              startIcon={<Icon icon={Plus} />}
              onClick={() => setIsAddModalOpen(true)}
            >
              {translate(LangEnum.GLOBAL_ADD)}
            </Button>
          )}
        </Stack>

        {emptyLabel ? (
          <Typography
            variant="body2"
            testId={CatalogTestIds.CATALOG_CREATION_MODAL_ACCESS_EMPTY}
            sx={{ color: 'text.secondary' }}
          >
            {translate(emptyLabel)}
          </Typography>
        ) : (
          <CatalogCreationAccessTable
            rows={rows}
            maxVisibleRows={maxVisibleRows}
            canRemoveEntity={canRemoveEntity}
            canEditDates={canEditDates}
            selection={selection}
            onEndReached={onEndReached}
            onChangeRowDate={onChangeRowDate}
            onRemoveRow={onRemoveRow}
          />
        )}
      </Stack>

      {isAddModalOpen && (
        <CatalogCreationAccessAddModal
          field={selectedField}
          catalogDates={catalogDates}
          listedEntityIds={listedEntityIds}
          catalogId={catalogId}
          onClose={() => setIsAddModalOpen(false)}
          onConfirm={handleAddEntities}
        />
      )}
    </>
  );
};

export default CatalogAccessEditor;
